import { Box, Divider, Stack, Typography } from '@mui/material';
import { NumericEngine } from '../../utility/helpers/numericEngine';
import { formatTaxPercent } from '../../utility/helpers/taxCalculation';
import { useTaxDecimalPrecision } from '../../utility/hooks/useTaxDecimalPrecision';

/**
 * The "Applicable items / Taxable amount / rate / Total amount" hover card for one tax row.
 * Single source of truth for this card so the editable payment summary (MultiTaxRows) and the
 * read-only detail summaries (TaxSummaryHover) can never drift apart visually.
 *
 * `summary` is one row from EITHER summarizeTaxAcrossLines() (live editor — recomputed from the
 * current lines) or summarizeTaxFromRecord() (detail pages — stored per-line amounts from the GET).
 * Both shapes expose the same fields this card reads: name, rate, appliedLines, totalLines,
 * taxableAmount, taxAmount.
 *
 * `name` / `rate` are optional overrides for callers that hold a fresher value than the summary —
 * MultiTaxRows passes the live global row, whose rate the user may be mid-edit on.
 */
const TaxBreakdownCard = ({ summary, name, rate }) => {
    const precision = useTaxDecimalPrecision();
    const taxName = name ?? summary?.name ?? '';
    const taxRate = rate ?? summary?.rate ?? 0;

    const fmtMoney = (n) => (Number(n) > 0 ? NumericEngine.format(n, { formatCurrency: true, trailingZeros: true }) : '$0.00');

    return (
        <Box sx={{ p: '10px 12px', minWidth: 214 }}>
            <Stack gap={'10px'}>
                <Stack direction="row" justifyContent="space-between" gap={3}>
                    <Typography sx={{ fontSize: 12, color: '#212121', lineHeight: '150%' }}>Applicable items</Typography>
                    <Typography sx={{ fontSize: 12 }}>
                        {summary?.appliedLines} of {summary?.totalLines}
                    </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" gap={3}>
                    <Typography sx={{ fontSize: 12, color: '#212121', lineHeight: '150%' }}>Taxable amount</Typography>
                    <Typography sx={{ fontSize: 12 }}>{fmtMoney(summary?.taxableAmount)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" gap={3}>
                    <Typography sx={{ fontSize: 12, color: '#212121', lineHeight: '150%' }}>{taxName} rate</Typography>
                    <Typography sx={{ fontSize: 12 }}>{formatTaxPercent(taxRate, precision)}%</Typography>
                </Stack>
                <Divider sx={{ my: '0' }} />
                <Stack direction="row" justifyContent="space-between" gap={3}>
                    <Typography sx={{ fontSize: 12, color: '#212121', fontWeight: 500 }}>Total {taxName} amount</Typography>
                    <Typography sx={{ fontSize: 12, color: '#212121', fontWeight: 500 }}>{fmtMoney(summary?.taxAmount)}</Typography>
                </Stack>
            </Stack>
        </Box>
    );
};

export default TaxBreakdownCard;
